import { useState, useEffect, useMemo, useCallback, useRef, type JSX } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellStyle,
  type ColDef,
  type ICellRendererParams,
} from 'ag-grid-community';
import { ChevronLeft, Save, Send, Check, Scan, Printer } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { useSessionTabs } from '../../hooks/useSessionTabs';
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import ArbiSelect from '../../components/ui/ArbiSelect';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import NotesPanel from '../../components/notes/NotesPanel';
import { Toast, type ToastState } from '../../components/ui/Toast';
import { createNotification, notifySubmitterAndAdmins } from '../../lib/notifications';
// Day 76 P5.1 Phase 9B — PATH_LABEL retired; pathLabel helper below
// derives from useSettingsConfig().activePaths.
import { fetchReceivingHistory, type ReceivingHistoryRow } from '../../lib/receiving';
import { extractEnvelope } from '../../lib/extractEnvelope';
import type { PostgrestError } from '@supabase/supabase-js';

ModuleRegistry.registerModules([AllCommunityModule]);

// Loose RPC caller for submit_receiving_record. Day 84.6 P4: error type widened
// to PostgrestError | null to compose with extractEnvelope Path 1 translation.
type LooseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: PostgrestError | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as LooseRpc;

// ── Formatting ──────────────────────────────────────────────────────────────

function fmt$(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

type ConditionValue = 'new' | 'damaged' | 'wrong_item' | 'missing';

const CONDITION_OPTIONS: { value: ConditionValue; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'wrong_item', label: 'Wrong Item' },
  { value: 'missing', label: 'Missing' },
];

// ── Row types ───────────────────────────────────────────────────────────────

interface PORow {
  id: string;
  po_number: string;
  order_date: string | null;
  expected_delivery_date: string | null;
  total_skus: number | null;
  total_units: number | null;
  distributor_id: string;
  submitted_by: string | null;
  distributors?: { name: string | null } | null;
}

interface LineItemRow {
  id: string;
  product_id: string | null;
  isg_sku: string | null;
  qty_ordered: number | null;
  cost_per_unit: number | null;
  products?: { title?: string | null; brand?: string | null; upc?: string | null } | null;
}

interface ReceivingDataEntry {
  received_qty: number;
  condition: ConditionValue | string;
}

type ReceivingDataMap = Record<string, ReceivingDataEntry>;

// Grid row shape (combined LineItemRow + receiving data)
interface GridRow extends LineItemRow {
  title: string;
  brand: string;
  expected_qty: number;
  received_qty: number;
  condition: string;
  has_discrepancy: boolean;
}

// ── Receiving Detail ────────────────────────────────────────────────────────

export default function ReceivingDetail(): JSX.Element {
  const { poId } = useParams();
  const navigate = useNavigate();
  // Day 64.5 V-ROUI: viewer is read-only on ReceivingDetail. isViewer folds
  // into the existing isReadOnly derivation at L448 so every gate already
  // wired against isReadOnly (Save Draft, Submit Receiving, qty editor,
  // condition editor, canSubmit guard) automatically covers viewer.
  const { user, isViewer, isDistRep } = useApp();
  // Day 76 P5.1 Phase 9B — path label lookup replacing hardcoded PATH_LABEL.
  // Falls back to path_key (lowercase) for historical-data carve-out (Day
  // 60b *_FALLBACK + deactivated/legacy paths not in fpc).
  const { activePaths } = useSettingsConfig();
  const pathLabel = useCallback(
    (path: string): string => activePaths.find((p) => p.path_key === path)?.short_label ?? path,
    [activePaths]
  );
  const { updateTabLabel } = useSessionTabs();

  // ── State ──────────────────────────────────────────────────
  const [po, setPo] = useState<PORow | null>(null);
  const [lineItems, setLineItems] = useState<LineItemRow[]>([]);
  const [receivingData, setReceivingData] = useState<ReceivingDataMap>({});
  // Per-path expected totals aggregated across every line item on this PO.
  // Empty when the PO pre-dates the allocation feature. Displayed as a
  // read-only hint in the PO Info card.
  const [expectedByPath, setExpectedByPath] = useState<Record<string, number>>({});
  const [draftRecordId, setDraftRecordId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  // Day 57 — Print PDF action-in-flight state. Disables the Print button
  // while @react-pdf/renderer + the Document component lazy-load on first
  // click; subsequent clicks are instant once the chunk is cached.
  const [printingPdf, setPrintingPdf] = useState<boolean>(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [confirmMessage, setConfirmMessage] = useState<string>('');
  const [recordStatus, setRecordStatus] = useState<'draft' | 'submitted'>('draft');
  const [receivingHistory, setReceivingHistory] = useState<ReceivingHistoryRow[]>([]);
  const [scanValue, setScanValue] = useState<string>('');
  const [flashRowId, setFlashRowId] = useState<string | null>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);

  // ── Load data ──────────────────────────────────────────────
  useEffect(() => {
    if (!poId) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId]);

  // Receiving history (PO-scoped). Reload alongside any draftRecordId change so
  // submitting the current draft re-fetches with submitted status.
  useEffect(() => {
    if (!poId) return;
    void fetchReceivingHistory(poId).then(setReceivingHistory);
  }, [poId, draftRecordId, recordStatus]);

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const { data: poData, error: poErr } = await supabase
        .from('purchase_orders')
        .select('*, distributors(name)')
        .eq('id', poId!)
        .single();
      if (poErr) throw poErr;
      const poRow = poData as unknown as PORow;
      setPo(poRow);

      if (poRow?.po_number) {
        updateTabLabel(`/purchasing/receiving/${poId}`, `Receiving — ${poRow.po_number}`);
      }

      // Architecture Rule 4: dist_rep sees zero pricing data. Project
      // cost_per_unit out of the SELECT for dist_rep — defense-in-depth
      // alongside the right-panel Total Value hide. The summary calc
      // gracefully handles a missing cost_per_unit (fallback to 0 in the
      // Number(...) coercion downstream), and the result is hidden anyway.
      const lineItemSelect = isDistRep
        ? 'id, product_id, isg_sku, qty_ordered, products(title, brand, upc)'
        : 'id, product_id, isg_sku, qty_ordered, cost_per_unit, products(title, brand, upc)';
      const { data: items, error: itemsErr } = await supabase
        .from('po_line_items')
        .select(lineItemSelect)
        .eq('po_id', poId!);
      if (itemsErr) throw itemsErr;
      const itemRows = (items ?? []) as unknown as LineItemRow[];
      setLineItems(itemRows);

      // Load per-path expected allocations for this PO's line items and
      // aggregate to a path→total map for display. Quietly tolerates POs
      // that pre-date the allocation feature (empty result → empty map).
      if (itemRows.length) {
        const ids = itemRows.map((r) => r.id);
        const { data: allocRows } = await supabase
          .from('po_line_item_allocations')
          .select('line_item_id, fulfillment_path, quantity')
          .in('line_item_id', ids);
        const totals: Record<string, number> = {};
        for (const row of (allocRows ?? []) as Array<{
          fulfillment_path: string | null;
          quantity: number | null;
        }>) {
          const path = row.fulfillment_path;
          const qty = Number(row.quantity) || 0;
          if (!path || qty <= 0) continue;
          totals[path] = (totals[path] || 0) + qty;
        }
        setExpectedByPath(totals);
      }

      // Check for existing draft
      const { data: drafts } = await supabase
        .from('receiving_records')
        .select('id, status')
        .eq('po_id', poId!)
        .eq('status', 'draft')
        .limit(1);

      const draftRows = (drafts ?? []) as { id: string; status: string }[];
      if (draftRows.length > 0) {
        const draft = draftRows[0];
        setDraftRecordId(draft.id);
        setRecordStatus('draft');

        const { data: draftLines } = await supabase
          .from('receiving_line_items')
          .select('po_line_item_id, received_qty, condition')
          .eq('receiving_record_id', draft.id);

        if (draftLines) {
          const map: ReceivingDataMap = {};
          for (const dl of draftLines as unknown as {
            po_line_item_id: string;
            received_qty: number;
            condition: string;
          }[]) {
            map[dl.po_line_item_id] = {
              received_qty: dl.received_qty,
              condition: dl.condition,
            };
          }
          setReceivingData(map);
        }
      } else {
        const { data: submitted } = await supabase
          .from('receiving_records')
          .select('id, status')
          .eq('po_id', poId!)
          .eq('status', 'submitted')
          .limit(1);

        const submittedRows = (submitted ?? []) as { id: string; status: string }[];
        if (submittedRows.length > 0) {
          setRecordStatus('submitted');
          setDraftRecordId(submittedRows[0].id);

          const { data: subLines } = await supabase
            .from('receiving_line_items')
            .select('po_line_item_id, received_qty, condition')
            .eq('receiving_record_id', submittedRows[0].id);

          if (subLines) {
            const map: ReceivingDataMap = {};
            for (const sl of subLines as unknown as {
              po_line_item_id: string;
              received_qty: number;
              condition: string;
            }[]) {
              map[sl.po_line_item_id] = {
                received_qty: sl.received_qty,
                condition: sl.condition,
              };
            }
            setReceivingData(map);
          }
        } else {
          const map: ReceivingDataMap = {};
          for (const item of itemRows) {
            map[item.id] = { received_qty: 0, condition: 'new' };
          }
          setReceivingData(map);
        }
      }
    } catch (e) {
      console.error('[ReceivingDetail] load error:', e);
      setToast({ kind: 'error', message: 'Failed to load receiving data' });
    } finally {
      setLoading(false);
    }
  }

  // ── Update handlers ───────────────────────────────────────
  const updateReceivedQty = useCallback((lineItemId: string, delta: number): void => {
    setReceivingData((prev) => {
      const current = prev[lineItemId] || { received_qty: 0, condition: 'new' };
      const newQty = Math.max(0, current.received_qty + delta);
      return { ...prev, [lineItemId]: { ...current, received_qty: newQty } };
    });
  }, []);

  const setReceivedQty = useCallback((lineItemId: string, qty: number): void => {
    setReceivingData((prev) => {
      const current = prev[lineItemId] || { received_qty: 0, condition: 'new' };
      return { ...prev, [lineItemId]: { ...current, received_qty: Math.max(0, qty) } };
    });
  }, []);

  const updateCondition = useCallback((lineItemId: string, condition: string): void => {
    setReceivingData((prev) => {
      const current = prev[lineItemId] || { received_qty: 0, condition: 'new' };
      return { ...prev, [lineItemId]: { ...current, condition } };
    });
  }, []);

  // Auto-focus the barcode scan input once the PO + line items are loaded
  // so a hand scanner inputs land directly without a manual click.
  useEffect(() => {
    if (!loading && lineItems.length > 0 && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [loading, lineItems.length]);

  const handleScan = useCallback(
    (raw: string): void => {
      const trimmed = raw.trim();
      if (!trimmed) return;
      const match = lineItems.find((li) => li.isg_sku === trimmed || li.products?.upc === trimmed);
      if (!match) {
        setToast({ kind: 'error', message: `No match for "${trimmed}"` });
        setScanValue('');
        return;
      }
      // Increment received_qty for the matched line item
      setReceivingData((prev) => {
        const current = prev[match.id] || { received_qty: 0, condition: 'new' };
        return {
          ...prev,
          [match.id]: { ...current, received_qty: current.received_qty + 1 },
        };
      });
      setFlashRowId(match.id);
      setTimeout(() => setFlashRowId(null), 600);
      setScanValue('');
      // Short success beep — Audio API; silently no-op when blocked
      try {
        const Ctx =
          (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
        osc.start();
        osc.stop(ctx.currentTime + 0.15);
      } catch {
        /* audio not available */
      }
    },
    [lineItems]
  );

  // ── Computed summary ──────────────────────────────────────
  const summary = useMemo(() => {
    const totalExpected = lineItems.reduce((s, item) => s + (item.qty_ordered || 0), 0);
    const totalExpectedSkus = lineItems.length;

    let totalReceived = 0;
    let receivedSkus = 0;
    let discrepancyCount = 0;
    let totalReceivedValue = 0;
    let totalExpectedValue = 0;

    for (const item of lineItems) {
      const rd = receivingData[item.id] || { received_qty: 0, condition: 'new' };
      totalReceived += rd.received_qty;
      totalExpectedValue += (item.qty_ordered || 0) * (Number(item.cost_per_unit) || 0);
      totalReceivedValue += rd.received_qty * (Number(item.cost_per_unit) || 0);
      if (rd.received_qty > 0) receivedSkus++;
      if (rd.received_qty !== (item.qty_ordered || 0) || rd.condition !== 'new') {
        discrepancyCount++;
      }
    }

    return {
      totalExpected,
      totalExpectedSkus,
      totalReceived,
      receivedSkus,
      discrepancyCount,
      totalReceivedValue,
      totalExpectedValue,
    };
  }, [lineItems, receivingData]);

  // ── Row data for AG Grid ──────────────────────────────────
  const rowData = useMemo<GridRow[]>(() => {
    return lineItems.map((item) => {
      const rd = receivingData[item.id] || { received_qty: 0, condition: 'new' };
      return {
        ...item,
        title: item.products?.title || '—',
        brand: item.products?.brand || '—',
        expected_qty: item.qty_ordered || 0,
        received_qty: rd.received_qty,
        condition: rd.condition,
        has_discrepancy: rd.received_qty !== (item.qty_ordered || 0) || rd.condition !== 'new',
      };
    });
  }, [lineItems, receivingData]);

  // ── AG Grid setup ─────────────────────────────────────────

  const isReadOnly = recordStatus === 'submitted' || isViewer;

  type RowParams = ICellRendererParams<GridRow>;

  const SkuCell = useCallback((p: RowParams) => {
    if (!p.data) return null;
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (p.data!.isg_sku) navigator.clipboard.writeText(p.data!.isg_sku);
        }}
        className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
        title="Click to copy"
      >
        {p.data.isg_sku}
      </button>
    );
  }, []);

  const QtyStepperCell = useCallback(
    (p: RowParams) => {
      if (!p.data) return null;
      const lineItemId = p.data.id;
      if (isReadOnly) {
        return <span className="font-mono">{p.data.received_qty}</span>;
      }
      return (
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              updateReceivedQty(lineItemId, -1);
            }}
            className="w-6 h-6 rounded bg-surface-4 hover:bg-surface-3 text-ink-primary flex items-center justify-center text-sm font-medium cursor-pointer transition-colors"
            aria-label="Decrease received quantity"
          >
            −
          </button>
          <input
            type="text"
            inputMode="numeric"
            value={p.data.received_qty}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) setReceivedQty(lineItemId, v);
            }}
            className="w-10 text-center font-mono text-xs bg-surface-3 border border-border rounded px-1 py-0.5"
            onClick={(e) => e.stopPropagation()}
            aria-label="Received quantity"
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              updateReceivedQty(lineItemId, 1);
            }}
            className="w-6 h-6 rounded bg-surface-4 hover:bg-surface-3 text-ink-primary flex items-center justify-center text-sm font-medium cursor-pointer transition-colors"
            aria-label="Increase received quantity"
          >
            +
          </button>
        </div>
      );
    },
    [isReadOnly, updateReceivedQty, setReceivedQty]
  );

  const ConditionCell = useCallback(
    (p: RowParams) => {
      if (!p.data) return null;
      if (isReadOnly) {
        const label =
          CONDITION_OPTIONS.find((o) => o.value === p.data!.condition)?.label || p.data.condition;
        return <span className="text-xs">{label}</span>;
      }
      return (
        // Wrapper div only exists to stop click propagation to row click.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div onClick={(e) => e.stopPropagation()}>
          <ArbiSelect
            value={p.data.condition}
            onChange={(val) => updateCondition(p.data!.id, val)}
            options={CONDITION_OPTIONS}
            size="xs"
            portal={true}
          />
        </div>
      );
    },
    [isReadOnly, updateCondition]
  );

  const DiscrepancyCell = useCallback((p: RowParams) => {
    if (!p.data) return null;
    if (p.data.has_discrepancy) {
      return (
        <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-danger/10 text-danger">
          Discrepancy
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs font-medium bg-success/10 text-success">
        <Check size={10} /> Match
      </span>
    );
  }, []);

  const columnDefs = useMemo<ColDef<GridRow>[]>(
    () => [
      {
        headerName: 'ISG SKU',
        field: 'isg_sku',
        minWidth: 110,
        cellRenderer: SkuCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Title',
        field: 'title',
        flex: 1,
        minWidth: 140,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontWeight: 500,
          color: 'var(--ink-bright)',
        } as CellStyle,
        tooltipField: 'title',
      },
      {
        headerName: 'Expected',
        field: 'expected_qty',
        minWidth: 80,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Received',
        field: 'received_qty',
        minWidth: 110,
        sortable: false,
        cellRenderer: QtyStepperCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Condition',
        field: 'condition',
        minWidth: 120,
        sortable: false,
        cellRenderer: ConditionCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Discrepancy',
        field: 'has_discrepancy',
        minWidth: 95,
        sortable: false,
        cellRenderer: DiscrepancyCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
    ],
    [SkuCell, QtyStepperCell, ConditionCell, DiscrepancyCell]
  );

  const defaultColDef = useMemo<ColDef<GridRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)', width: 1, style: 'solid' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerHeight: 36,
        rowHeight: 48,
        spacing: 4,
      }),
    []
  );

  // ── Save draft ────────────────────────────────────────────
  const handleSaveDraft = async (): Promise<void> => {
    if (!user) return;
    setSubmitting(true);
    try {
      // Cluster 3b (FIX Session 2) — ONE atomic RPC upserts the draft record +
      // replaces its line items, so a failure after the line DELETE never loses the
      // draft's lines (was: insert-record → DELETE lines → INSERT lines, non-atomic).
      const lines = lineItems.map((item) => {
        const rd = receivingData[item.id] || { received_qty: 0, condition: 'new' };
        return {
          po_line_item_id: item.id,
          product_id: item.product_id,
          isg_sku: item.isg_sku,
          expected_qty: item.qty_ordered || 0,
          received_qty: rd.received_qty,
          condition: rd.condition,
        };
      });

      const env = extractEnvelope<{ record_id: string }>(
        await callRpc('save_receiving_draft', {
          p_po_id: poId,
          p_record_id: draftRecordId,
          p_lines: lines,
        })
      );
      if (!env.isOk || !env.data) {
        throw new Error(env.userErrors[0]?.message ?? 'Failed to save draft');
      }
      setDraftRecordId(env.data.record_id);
      setRecordStatus('draft');
      setToast({ kind: 'success', message: 'Draft saved.' });
    } catch (e) {
      console.error('[ReceivingDetail] save draft error:', e);
      setToast({
        kind: 'error',
        message: `Failed to save draft: ${e instanceof Error ? e.message : String(e)}`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Submit receiving ──────────────────────────────────────
  /**
   * Day 57 — Print PDF. Dynamic-imports @react-pdf/renderer + the Document
   * component (per ADR Decision 32) so the lib only lands on first click.
   * Works in worksheet / draft / submitted states; the Document component
   * leaves Received/Discrepancy blank when recordStatus is null.
   */
  const handlePrintPdf = async (): Promise<void> => {
    if (!po || lineItems.length === 0) return;
    setPrintingPdf(true);
    try {
      const [{ pdf }, { default: ReceivingSheetPdfDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../../components/pdf/ReceivingSheetPdfDocument'),
      ]);
      // Day 76 P5.1 Phase 9B — pass live path labels derived from
      // useSettingsConfig().activePaths (fpc.display_name) so the PDF
      // mirrors the operator's live label set instead of the local
      // PATH_LABEL_FALLBACK const.
      const pathLabels = Object.fromEntries(activePaths.map((p) => [p.path_key, p.display_name]));
      const blob = await pdf(
        <ReceivingSheetPdfDocument
          po={po}
          lineItems={lineItems}
          receivingData={receivingData}
          expectedByPath={expectedByPath}
          recordStatus={recordStatus}
          pathLabels={pathLabels}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Receiving-${po.po_number}-${recordStatus ?? 'worksheet'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[ReceivingDetail] print PDF error:', e);
      setToast({
        kind: 'error',
        message: 'PDF generation failed. Try again, or contact support if this continues.',
      });
    } finally {
      setPrintingPdf(false);
    }
  };

  const handleSubmitClick = (): void => {
    if (summary.discrepancyCount > 0) {
      setConfirmMessage(
        `${summary.discrepancyCount} discrepanc${summary.discrepancyCount === 1 ? 'y' : 'ies'} found. This will flag the PO. Continue?`
      );
    } else if (summary.totalReceived >= summary.totalExpected) {
      setConfirmMessage(`Submit receiving for ${po?.po_number}? This cannot be undone.`);
    } else {
      setConfirmMessage(
        `${summary.totalReceived} of ${summary.totalExpected} units received. This will mark the PO as Partially Received.`
      );
    }
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async (): Promise<void> => {
    if (!user) return;
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      let recordId = draftRecordId;

      if (!recordId) {
        const { data: rec, error: recErr } = await supabase
          .from('receiving_records')
          .insert({ po_id: poId!, received_by: user.id, status: 'draft' } as never)
          .select('id')
          .single();
        if (recErr) throw recErr;
        recordId = (rec as { id: string }).id;
        setDraftRecordId(recordId);
      }

      await supabase.from('receiving_line_items').delete().eq('receiving_record_id', recordId);

      const lines = lineItems.map((item) => {
        const rd = receivingData[item.id] || { received_qty: 0, condition: 'new' };
        return {
          receiving_record_id: recordId,
          po_line_item_id: item.id,
          product_id: item.product_id,
          isg_sku: item.isg_sku,
          expected_qty: item.qty_ordered || 0,
          received_qty: rd.received_qty,
          condition: rd.condition,
        };
      });

      const { error: linesErr } = await supabase
        .from('receiving_line_items')
        .insert(lines as never);
      if (linesErr) throw linesErr;

      // Day 84.6 P4.1 — submit_receiving_record envelope-extract per Decision 45 +
      // Path B audit 🔴 silent-broken fix. Pre-Phase-1 returned text status;
      // post-migration returns { ok, data: { status }, userErrors } jsonb envelope.
      // Direct `newStatus === 'received'` comparison was broken (envelope object
      // vs string).
      const recvResult = await callRpc('submit_receiving_record', {
        p_record_id: recordId,
      });
      const recvEnv = extractEnvelope<{ status: string }>(recvResult);
      if (!recvEnv.isOk) {
        throw new Error(recvEnv.userErrors[0]?.message ?? 'Receiving submit failed');
      }
      const newStatus = recvEnv.data!.status;

      // Hard draw fires inside submit_receiving_record on full receipt; check
      // post-state and fan out low_credit_warning if available credit dropped
      // through the configured danger threshold.
      // F1-A (Day 99a): the low-credit alert moved server-side to the check-low-credit Edge
      // Function (service-role) so the dist_rep receiving path no longer reads the now-gated
      // get_credit_summary client-side. Fire-and-forget — failures never block receiving.
      if (po?.distributor_id && newStatus === 'received') {
        supabase.functions
          .invoke('check-low-credit', {
            body: {
              distributor_id: po.distributor_id,
              distributor_name: po.distributors?.name ?? 'Distributor',
            },
          })
          .then(
            ({ error }) => {
              if (error) console.error('[Arbi] check-low-credit invoke error:', error.message);
            },
            (e: unknown) => console.error('[Arbi] check-low-credit invoke exception:', e)
          );
      }

      const { data: admins } = await supabase.from('profiles').select('id').eq('role', 'admin');
      const adminRows = (admins ?? []) as { id: string }[];
      if (newStatus === 'received') {
        for (const a of adminRows) {
          createNotification({
            userId: a.id,
            event: 'po_received',
            title: 'PO Received',
            message: `PO ${po?.po_number} fully received.`,
            link: `/purchasing/purchase-orders/${po?.id}`,
          }).catch(() => {});
        }
      } else if (newStatus === 'discrepancy') {
        for (const a of adminRows) {
          createNotification({
            userId: a.id,
            event: 'receiving_discrepancy',
            title: 'Receiving Discrepancy',
            message: `PO ${po?.po_number} has receiving discrepancies.`,
            link: `/purchasing/discrepancies`,
          }).catch(() => {});
        }
        // Sibling fan-out: po_discrepancy_flagged is the PO-level dedicated
        // event (vs receiving_discrepancy's queue-level fan-out above) — same
        // moment, different recipient set + deep link to the affected PO so
        // the submitter can act.
        notifySubmitterAndAdmins({
          submitterUserId: po?.submitted_by,
          event: 'po_discrepancy_flagged',
          title: `PO ${po?.po_number} discrepancy flagged`,
          message: 'A discrepancy has been recorded against this PO during receiving.',
          link: po?.id ? `/purchasing/purchase-orders/${po.id}` : '/purchasing/discrepancies',
        });
      }

      setToast({ kind: 'success', message: 'Receiving submitted.' });
      setRecordStatus('submitted');

      setTimeout(() => navigate('/purchasing/receiving'), 1500);
    } catch (e) {
      console.error('[ReceivingDetail] submit error:', e);
      setToast({
        kind: 'error',
        message: `Failed to submit: ${e instanceof Error ? e.message : String(e)}`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Can submit check ──────────────────────────────────────
  const canSubmit = useMemo(() => {
    if (isReadOnly) return false;
    return lineItems.some((item) => {
      const rd = receivingData[item.id];
      return rd && rd.received_qty > 0;
    });
  }, [lineItems, receivingData, isReadOnly]);

  // ── Discrepant lines for summary ──────────────────────────
  const discrepantLines = useMemo(() => {
    return rowData.filter((r) => r.has_discrepancy);
  }, [rowData]);

  if (loading) {
    return (
      <div className="flex flex-col h-full -m-4">
        <div className="flex-1 flex items-center justify-center">
          <span className="text-ink-muted text-sm">Loading receiving data…</span>
        </div>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="flex flex-col h-full -m-4">
        <div className="flex-1 flex items-center justify-center">
          <span className="text-ink-muted text-sm">PO not found</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full -m-4">
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      <ConfirmDialog
        open={confirmOpen}
        title="Submit Receiving"
        message={confirmMessage}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setConfirmOpen(false)}
      />

      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <button
          onClick={() => navigate('/purchasing/receiving')}
          className="flex items-center gap-0.5 text-xs text-ink-muted hover:text-ink-primary transition-colors cursor-pointer mb-1"
        >
          <ChevronLeft size={12} /> Back to Receiving
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-base font-semibold text-ink-bright">Receiving — {po.po_number}</h1>
            <span
              className={clsx(
                'px-2 py-0.5 rounded text-2xs font-medium',
                recordStatus === 'submitted'
                  ? 'bg-success/10 text-success'
                  : 'bg-surface-4 text-ink-muted'
              )}
            >
              {recordStatus === 'submitted' ? 'Submitted' : 'Draft'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {!isReadOnly && (
              <>
                <button
                  onClick={handleSaveDraft}
                  className="arbi-btn-ghost text-xs"
                  disabled={submitting}
                >
                  <Save size={14} /> Save Draft
                </button>
                <button
                  onClick={handleSubmitClick}
                  className="arbi-btn-primary text-xs"
                  disabled={submitting || !canSubmit}
                >
                  <Send size={14} /> Submit Receiving
                </button>
              </>
            )}
            {/* Day 57 — Print PDF outside the !isReadOnly wrapper so submitted
                records can re-print as audit copies, and worksheet state can
                print blank rows for warehouse-floor handwriting. */}
            <button
              onClick={handlePrintPdf}
              className="arbi-btn-ghost text-xs"
              disabled={!po || lineItems.length === 0 || printingPdf}
              data-testid="receiving-print-pdf-button"
            >
              <Printer size={14} /> {printingPdf ? 'Generating…' : 'Print PDF'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex gap-4">
          <div className="flex-1 min-w-0 space-y-4">
            <div className="arbi-card p-4">
              <h2 className="text-sm font-semibold text-ink-bright mb-3">PO Info</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-2xs text-ink-muted block mb-0.5">PO Number</span>
                  <span className="text-xs font-mono bg-surface-2 px-2 py-1 rounded block">
                    {po.po_number}
                  </span>
                </div>
                <div>
                  <span className="text-2xs text-ink-muted block mb-0.5">Distributor</span>
                  <span className="text-xs bg-surface-2 px-2 py-1 rounded block">
                    {po.distributors?.name || '—'}
                  </span>
                </div>
                <div>
                  <span className="text-2xs text-ink-muted block mb-0.5">Order Date</span>
                  <span className="text-xs font-mono bg-surface-2 px-2 py-1 rounded block">
                    {fmtDate(po.order_date)}
                  </span>
                </div>
                <div>
                  <span className="text-2xs text-ink-muted block mb-0.5">Expected Delivery</span>
                  <span className="text-xs font-mono bg-surface-2 px-2 py-1 rounded block">
                    {fmtDate(po.expected_delivery_date)}
                  </span>
                </div>
                <div>
                  <span className="text-2xs text-ink-muted block mb-0.5">Total SKUs Ordered</span>
                  <span className="text-xs font-mono bg-surface-2 px-2 py-1 rounded block">
                    {po.total_skus || lineItems.length}
                  </span>
                </div>
                <div>
                  <span className="text-2xs text-ink-muted block mb-0.5">Total Units Ordered</span>
                  <span className="text-xs font-mono bg-surface-2 px-2 py-1 rounded block">
                    {po.total_units || summary.totalExpected}
                  </span>
                </div>
              </div>
              {/* Expected allocation summary — only shown when the PO was
                  built with per-path allocations. POs created before the
                  allocation feature launched will have an empty map and this
                  block quietly hides. */}
              {Object.keys(expectedByPath).length > 0 && (
                <div className="mt-3 pt-3 border-t border-border-subtle">
                  <span className="text-2xs text-ink-muted block mb-1.5">Expected Allocation</span>
                  <div className="flex flex-wrap gap-1.5">
                    {/* Day 75 (D5) — iterate expectedByPath keys directly so
                        a deactivated path's historical allocations still
                        display (Sol historical-data carve-out). PATH_LABEL
                        falls back to path_key when label not in map. */}
                    {Object.entries(expectedByPath)
                      .filter(([, v]) => (v || 0) > 0)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([p, units]) => (
                        <span
                          key={p}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-surface-3 text-xs"
                        >
                          <span className="text-ink-muted">{pathLabel(p)}</span>
                          <span
                            className="font-mono text-ink-bright"
                            style={{ fontFamily: "'JetBrains Mono', monospace" }}
                          >
                            ×{units}
                          </span>
                        </span>
                      ))}
                  </div>
                  <p className="text-2xs text-ink-muted mt-1.5">
                    These are the expected per-path totals for this PO. Receiving-by-path workflow
                    arrives in a later build.
                  </p>
                </div>
              )}
            </div>

            {draftRecordId && <NotesPanel entityType="receiving_record" entityId={draftRecordId} />}

            <div className="arbi-card p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-ink-bright">Line Items</h2>
                {recordStatus === 'draft' && !isDistRep && (
                  <div className="relative w-72">
                    <Scan
                      size={13}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
                    />
                    <input
                      ref={scanInputRef}
                      value={scanValue}
                      onChange={(e) => setScanValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleScan(scanValue);
                        }
                      }}
                      placeholder="Scan UPC or ISG SKU…"
                      aria-label="Barcode scan input"
                      className="arbi-input pl-8 pr-3 py-1 text-xs w-full font-mono"
                    />
                  </div>
                )}
              </div>
              <div style={{ height: Math.min(48 * rowData.length + 36 + 4, 600) }}>
                <AgGridReact<GridRow>
                  theme={gridTheme}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowData={rowData}
                  animateRows={false}
                  headerHeight={36}
                  rowHeight={48}
                  suppressCellFocus={true}
                  suppressHorizontalScroll={true}
                  domLayout={rowData.length <= 10 ? 'autoHeight' : 'normal'}
                  getRowClass={(params) =>
                    params.data && flashRowId === params.data.id ? 'arbi-row-scan-flash' : ''
                  }
                />
              </div>
            </div>

            {/* Receiving History — partially-received POs accumulate multiple
                records over time. Current draft (or just-submitted) is highlighted. */}
            <div className="arbi-card p-4">
              <h2 className="text-sm font-semibold text-ink-bright mb-3">Receiving History</h2>
              {receivingHistory.length === 0 ? (
                <p className="text-xs text-ink-muted">No receiving records yet</p>
              ) : (
                <ul className="space-y-1.5">
                  {receivingHistory.map((h) => {
                    const isCurrent = h.id === draftRecordId;
                    return (
                      <li
                        key={h.id}
                        className={clsx(
                          'flex items-center justify-between text-xs px-2 py-1.5 rounded transition-colors',
                          isCurrent && 'bg-accent-muted/20 border border-accent/40'
                        )}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-mono text-ink-secondary whitespace-nowrap">
                            {fmtDate(h.received_at)}
                          </span>
                          <span className="text-ink-primary truncate">
                            {h.received_by_name || 'Unknown'}
                          </span>
                          <span
                            className={clsx(
                              'px-1.5 py-0.5 rounded text-2xs font-medium',
                              h.status === 'submitted'
                                ? 'bg-success/10 text-success'
                                : 'bg-surface-3 text-ink-muted'
                            )}
                          >
                            {h.status}
                          </span>
                          {isCurrent && (
                            <span className="text-2xs text-accent font-medium">current</span>
                          )}
                        </div>
                        <span className="text-ink-muted font-mono whitespace-nowrap">
                          {h.line_item_count} item{h.line_item_count !== 1 ? 's' : ''}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="w-72 flex-shrink-0">
            <div className="sticky top-0 space-y-4">
              <div className="arbi-card p-4">
                <h2 className="text-2xs uppercase tracking-wider text-ink-muted font-medium mb-3">
                  Receiving Summary
                </h2>
                <div className="space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Total SKUs</span>
                    <span className="font-mono text-ink-bright">
                      {summary.receivedSkus} / {summary.totalExpectedSkus}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Total Units</span>
                    <span className="font-mono text-ink-bright">
                      {summary.totalReceived} / {summary.totalExpected}
                    </span>
                  </div>
                  {/* Architecture Rule 4: dist_rep sees zero pricing data. */}
                  {!isDistRep && (
                    <div className="flex justify-between text-xs">
                      <span className="text-ink-muted">Total Value</span>
                      <span className="font-mono text-ink-bright">
                        {fmt$(summary.totalReceivedValue)} / {fmt$(summary.totalExpectedValue)}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-border-subtle my-2" />
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Discrepancies</span>
                    {summary.discrepancyCount > 0 ? (
                      <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-danger/10 text-danger">
                        {summary.discrepancyCount}
                      </span>
                    ) : (
                      <span className="text-success text-xs font-medium">None</span>
                    )}
                  </div>
                </div>
              </div>

              {summary.discrepancyCount > 0 && (
                <div className="arbi-card p-4 border-danger/20">
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-2xs uppercase tracking-wider text-ink-muted font-medium">
                      Discrepancies
                    </h2>
                    <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-danger/10 text-danger">
                      {summary.discrepancyCount}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {discrepantLines.map((line) => {
                      const rd = receivingData[line.id] || { received_qty: 0, condition: 'new' };
                      return (
                        <div
                          key={line.id}
                          className="flex items-center justify-between text-xs bg-surface-2 rounded px-2 py-1.5"
                        >
                          <span className="font-mono text-accent text-2xs">{line.isg_sku}</span>
                          <span className="font-mono text-ink-muted">
                            {rd.received_qty}/{line.expected_qty}
                          </span>
                          {rd.condition !== 'new' && (
                            <span className="text-danger text-2xs">{rd.condition}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
